import React, { useState, useEffect } from 'react';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch, HiOutlinePencil, HiOutlineTrash, HiOutlineUpload } from 'react-icons/hi';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../api/axiosConfig';
import './CustomersTab.css';

const CustomersTab = ({ canEdit, canDelete, canCreate }) => {
    const [customers, setCustomers] = useState([]);
    const [companyNames, setCompanyNames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [companyFilter, setCompanyFilter] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;
    const [formData, setFormData] = useState({
        ledger_code: '',
        name: '',
        email: '',
        cc: '',
        email_template: '',
        company_name: ''
    });

    useEffect(() => {
        fetchCustomers();
        fetchCompanyNames();
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [companyFilter]);

    // Debounce the search box so filtering 3000 rows doesn't re-run on every keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Whenever the result set changes, jump back to the first page.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, companyFilter]);

    const fetchCompanyNames = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/company-names');
            if (response.data.success && response.data.data?.company_names) {
                setCompanyNames(response.data.data.company_names);
            }
        } catch (error) {
            console.error('Error fetching company names:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const params = {};
            if (companyFilter) {
                params.company_name = companyFilter;
            }
            const response = await axiosInstance.get('/api/aging-reports/customers', { params });
            if (response.data.success) {
                setCustomers(response.data.data || []);
                // Reset selection whenever the underlying list reloads so we never
                // hold ledger codes that are no longer visible.
                setSelected(new Set());
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            alert('Failed to fetch customers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validate email format (supports comma-separated emails)
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            const emails = formData.email.split(',').map(e => e.trim());
            const invalidEmails = emails.filter(e => !emailRegex.test(e));
            
            if (invalidEmails.length > 0) {
                alert(`Invalid email format: ${invalidEmails.join(', ')}\n\nPlease use valid email addresses separated by commas.`);
                return;
            }

            // Clean formData: remove empty strings and undefined values
            const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            if (editingCustomer) {
                // Update customer
                await axiosInstance.put(`/api/aging-reports/customers/${editingCustomer.ledger_code}`, cleanedData);
                alert('Customer updated successfully');
            } else {
                // Add customer
                await axiosInstance.post('/api/aging-reports/customers', cleanedData);
                alert('Customer added successfully');
            }
            setShowModal(false);
            resetForm();
            fetchCustomers();
        } catch (error) {
            console.error('Error saving customer:', error);
            alert(error.response?.data?.message || 'Failed to save customer');
        }
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setFormData({
            ledger_code: customer.ledger_code || '',
            name: customer.name || '',
            email: customer.email || '',
            cc: customer.cc || '',
            email_template: customer.email_template || '',
            company_name: customer.company_name || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (ledgerCode) => {
        if (!window.confirm('Are you sure you want to delete this customer?')) return;
        
        try {
            await axiosInstance.delete(`/api/aging-reports/customers/${ledgerCode}`);
            alert('Customer deleted successfully');
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting customer:', error);
            alert('Failed to delete customer');
        }
    };

    const resetForm = () => {
        setFormData({
            ledger_code: '',
            name: '',
            email: '',
            cc: '',
            email_template: '',
            company_name: ''
        });
        setEditingCustomer(null);
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        if (!validTypes.includes(file.type)) {
            alert('Please upload a valid Excel file (.xlsx or .xls)');
            e.target.value = '';
            return;
        }

        try {
            setUploading(true);
            setUploadResult(null);
            const formData = new FormData();
            formData.append('file', file);

            const response = await axiosInstance.post('/api/aging-reports/customers/bulk-upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // The API reports per-row outcomes - show them instead of a blanket success
                const result = response.data.data || {};
                setUploadResult({
                    fileName: file.name,
                    summary: result.summary || {},
                    failedRecords: result.failed_records || [],
                    cleanedRecords: result.cleaned_records || []
                });
                fetchCustomers();
            }
        } catch (error) {
            console.error('Error uploading customers:', error);
            const detail = error.response?.data?.error?.detail;
            setUploadResult({
                fileName: file.name,
                error: detail || error.response?.data?.message || 'Failed to upload customers'
            });
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const closeBulkUploadModal = () => {
        setShowBulkUploadModal(false);
        setUploadResult(null);
    };

    const downloadSampleTemplate = () => {
        // Create sample data with headers
        const sampleData = [
            {
                ledger_code: 'CUST001',
                name: 'John Doe',
                email: 'john@example.com, john.doe@company.com',
                cc: 'manager@example.com',
                company_name: 'ABC Corp',
                email_template: ''
            },
            {
                ledger_code: 'CUST002',
                name: 'Jane Smith',
                email: 'jane@example.com',
                cc: '',
                company_name: 'XYZ Ltd',
                email_template: ''
            }
        ];

        // Create a new workbook
        const wb = XLSX.utils.book_new();
        
        // Convert data to worksheet
        const ws = XLSX.utils.json_to_sheet(sampleData);

        // Set column widths for better readability
        ws['!cols'] = [
            { wch: 15 }, // ledger_code
            { wch: 20 }, // name
            { wch: 30 }, // email
            { wch: 30 }, // cc
            { wch: 20 }, // company_name
            { wch: 30 }  // email_template
        ];

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Customers');

        // Generate Excel file and trigger download
        XLSX.writeFile(wb, 'customer_upload_template.xlsx');
    };

    const search = debouncedSearch.toLowerCase();
    const filteredCustomers = customers.filter(customer =>
        customer.ledger_code?.toLowerCase().includes(search) ||
        customer.name?.toLowerCase().includes(search) ||
        customer.email?.toLowerCase().includes(search)
    );

    // Client-side pagination over the filtered set.
    const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    const paginatedCustomers = filteredCustomers.slice(startIdx, startIdx + PAGE_SIZE);

    const allFilteredSelected =
        filteredCustomers.length > 0 &&
        filteredCustomers.every(c => selected.has(c.ledger_code));
    const someFilteredSelected = filteredCustomers.some(c => selected.has(c.ledger_code));

    const toggleSelectOne = (ledgerCode) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(ledgerCode)) next.delete(ledgerCode);
            else next.add(ledgerCode);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredCustomers.forEach(c => next.delete(c.ledger_code));
            } else {
                filteredCustomers.forEach(c => next.add(c.ledger_code));
            }
            return next;
        });
    };

    const handleBulkDelete = async () => {
        const codes = Array.from(selected);
        if (codes.length === 0) return;
        if (!window.confirm(`Delete ${codes.length} selected customer(s)? This action cannot be undone.`)) return;

        try {
            setLoading(true);
            const response = await axiosInstance.delete('/api/aging-reports/customers', {
                data: { ledger_codes: codes }
            });
            const deleted = response.data?.data?.deleted_count ?? codes.length;
            alert(`${deleted} customer(s) deleted successfully`);
            setSelected(new Set());
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting customers:', error);
            alert(error.response?.data?.message || 'Failed to delete customers');
            setLoading(false);
        }
    };

    return (
        <div className="customers-tab">
            <div className="tab-header">
                <h2>Customer Management</h2>
                <div className="tab-actions">
                    <div className="search-box">
                        <HiOutlineSearch />
                        <input
                            type="text"
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {canDelete && selected.size > 0 && (
                        <button onClick={handleBulkDelete} className="btn-bulk-delete">
                            <HiOutlineTrash /> Delete Selected ({selected.size})
                        </button>
                    )}
                    <button onClick={fetchCustomers} className="btn-icon" title="Refresh">
                        <HiOutlineRefresh />
                    </button>
                    {canCreate && (
                        <>
                            <button onClick={() => setShowBulkUploadModal(true)} className="btn-secondary">
                                <HiOutlineUpload /> Bulk Upload
                            </button>
                            <button onClick={() => setShowModal(true)} className="btn-primary">
                                <HiOutlinePlus /> Add Customer
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading customers...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                {canDelete && (
                                    <th className="checkbox-col">
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            ref={(el) => {
                                                if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                                            }}
                                            onChange={toggleSelectAll}
                                            title="Select all"
                                        />
                                    </th>
                                )}
                                <th>Ledger Code</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>CC</th>
                                <th>
                                    Type
                                    <select
                                        value={companyFilter}
                                        onChange={(e) => setCompanyFilter(e.target.value)}
                                        style={{
                                            marginLeft: '8px',
                                            padding: '2px 6px',
                                            fontSize: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="">All</option>
                                        {companyNames.map((item, index) => (
                                            <option key={index} value={item.value || item}>
                                                {item.value || item}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomers.length === 0 ? (
                                <tr>
                                    <td colSpan={canDelete ? 7 : 6} className="no-data">No customers found</td>
                                </tr>
                            ) : (
                                paginatedCustomers.map(customer => (
                                    <tr key={customer.ledger_code} className={selected.has(customer.ledger_code) ? 'row-selected' : ''}>
                                        {canDelete && (
                                            <td className="checkbox-col">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(customer.ledger_code)}
                                                    onChange={() => toggleSelectOne(customer.ledger_code)}
                                                />
                                            </td>
                                        )}
                                        <td>{customer.ledger_code}</td>
                                        <td>{customer.name}</td>
                                        <td>{customer.email}</td>
                                        <td>{customer.cc || '-'}</td>
                                        <td>{customer.company_name || '-'}</td>
                                        <td>
                                            <div className="action-buttons">
                                                {canEdit && (
                                                    <button onClick={() => handleEdit(customer)} className="btn-icon" title="Edit">
                                                        <HiOutlinePencil />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button onClick={() => handleDelete(customer.ledger_code)} className="btn-icon btn-danger" title="Delete">
                                                        <HiOutlineTrash />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {filteredCustomers.length > 0 && (
                        <div className="pagination-bar">
                            <span className="pagination-info">
                                Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filteredCustomers.length)} of {filteredCustomers.length}
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="btn-secondary"
                                    onClick={() => setPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    « First
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={() => setPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    ‹ Prev
                                </button>
                                <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                                <button
                                    className="btn-secondary"
                                    onClick={() => setPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next ›
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={() => setPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last »
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Ledger Code *</label>
                                <input
                                    type="text"
                                    value={formData.ledger_code}
                                    onChange={(e) => setFormData({ ...formData, ledger_code: e.target.value })}
                                    required
                                    disabled={editingCustomer}
                                />
                            </div>
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email(s) *</label>
                                <input
                                    type="text"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    placeholder="email1@example.com, email2@example.com"
                                />
                                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                    💡 You can enter multiple emails separated by commas
                                </small>
                            </div>
                            <div className="form-group">
                                <label>CC Emails (comma-separated)</label>
                                <input
                                    type="text"
                                    value={formData.cc}
                                    onChange={(e) => setFormData({ ...formData, cc: e.target.value })}
                                    placeholder="email1@example.com, email2@example.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>Type</label>
                                <select
                                    value={formData.company_name}
                                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                >
                                    <option value="">Select Type</option>
                                    {companyNames.map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {editingCustomer && (
                                <div className="form-group">
                                    <label>Email Template</label>
                                    <textarea
                                        value={formData.email_template}
                                        onChange={(e) => setFormData({ ...formData, email_template: e.target.value })}
                                        rows="4"
                                        placeholder="Dear {name}, your aging report is attached..."
                                    />
                                    <small className="template-help">
                                        💡 Use variables: <code>{'{name}'}</code>, <code>{'{ledger_code}'}</code>, <code>{'{company_name}'}</code> | New line: <code>\n</code>
                                        <br/>
                                        Example: Dear {'{name}'},\nYour report for {'{company_name}'} is attached.
                                    </small>
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingCustomer ? 'Update' : 'Add'} Customer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showBulkUploadModal && (
                <div className="modal-overlay" onClick={closeBulkUploadModal}>
                    <div className="modal-content bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>📤 Bulk Upload Customers</h3>

                        {uploadResult && (
                            <div className="upload-result">
                                {uploadResult.error ? (
                                    <div className="upload-result-banner error">
                                        <strong>❌ Upload failed</strong>
                                        <div className="upload-result-reason">{uploadResult.error}</div>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`upload-result-banner ${uploadResult.summary.failed > 0 ? 'partial' : 'success'}`}>
                                            <strong>
                                                {uploadResult.summary.failed > 0
                                                    ? `⚠️ Uploaded with ${uploadResult.summary.failed} failed row${uploadResult.summary.failed === 1 ? '' : 's'}`
                                                    : '✅ All rows uploaded successfully'}
                                            </strong>
                                            <div className="upload-result-file">{uploadResult.fileName}</div>
                                        </div>

                                        <div className="upload-result-stats">
                                            <span><b>{uploadResult.summary.total_rows ?? 0}</b> total</span>
                                            <span className="ok"><b>{uploadResult.summary.created ?? 0}</b> created</span>
                                            <span className="ok"><b>{uploadResult.summary.updated ?? 0}</b> updated</span>
                                            <span className={uploadResult.summary.failed > 0 ? 'bad' : ''}>
                                                <b>{uploadResult.summary.failed ?? 0}</b> failed
                                            </span>
                                            {uploadResult.summary.cleaned > 0 && (
                                                <span className="info"><b>{uploadResult.summary.cleaned}</b> auto-cleaned</span>
                                            )}
                                        </div>

                                        {uploadResult.failedRecords.length > 0 && (
                                            <div className="upload-result-section">
                                                <h4>❌ Rows that were not imported</h4>
                                                <p className="upload-result-hint">
                                                    Fix these rows in your Excel file and upload again. Row numbers match the Excel sheet.
                                                </p>
                                                <div className="upload-result-table">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Excel Row</th>
                                                                <th>Ledger Code</th>
                                                                <th>Reason</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {uploadResult.failedRecords.map((rec, i) => (
                                                                <tr key={i}>
                                                                    <td className="row-num">{rec.row}</td>
                                                                    <td>{rec.ledger_code || '-'}</td>
                                                                    <td className="reason">{rec.error}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {uploadResult.cleanedRecords.length > 0 && (
                                            <div className="upload-result-section">
                                                <h4>🧹 Rows that were auto-corrected</h4>
                                                <p className="upload-result-hint">
                                                    These were imported after removing formatting artifacts such as <code>mailto:</code> links.
                                                </p>
                                                <div className="upload-result-table">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Excel Row</th>
                                                                <th>Field</th>
                                                                <th>Saved As</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {uploadResult.cleanedRecords.map((rec, i) => (
                                                                <tr key={i}>
                                                                    <td className="row-num">{rec.row}</td>
                                                                    <td>{rec.field}</td>
                                                                    <td className="reason">{rec.after}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {!uploadResult && (
                        <div className="upload-instructions">
                            <div className="instruction-section">
                                <h4>📋 How to Upload</h4>
                                <ol>
                                    <li>Download the sample template below</li>
                                    <li>Fill in your customer data in the Excel file</li>
                                    <li>Make sure to use the exact column names</li>
                                    <li>Upload the completed file</li>
                                </ol>
                            </div>

                            <div className="instruction-section">
                                <h4>📊 Required Columns</h4>
                                <div className="column-list">
                                    <div className="column-item required">
                                        <strong>ledger_code</strong> - Unique customer ID (Required)
                                    </div>
                                    <div className="column-item required">
                                        <strong>name</strong> - Customer name (Required)
                                    </div>
                                    <div className="column-item required">
                                        <strong>email</strong> - Customer email address(es) (Required)
                                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                                            💡 Can be multiple emails separated by commas
                                        </div>
                                    </div>
                                    <div className="column-item optional">
                                        <strong>cc</strong> - CC email addresses, comma-separated (Optional)
                                    </div>
                                    <div className="column-item optional">
                                        <strong>company_name</strong> - Company name (Optional)
                                    </div>
                                    <div className="column-item optional">
                                        <strong>email_template</strong> - Custom email template (Optional)
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                            Use: <code>{'{name}'}</code>, <code>{'{ledger_code}'}</code>, <code>{'{company_name}'}</code>, <code>\n</code> for new line
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="instruction-section example">
                                <h4>💡 Example</h4>
                                <div className="example-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>ledger_code</th>
                                                <th>name</th>
                                                <th>email</th>
                                                <th>cc</th>
                                                <th>company_name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>CUST001</td>
                                                <td>John Doe</td>
                                                <td>john@example.com, john.doe@company.com</td>
                                                <td>manager@example.com</td>
                                                <td>ABC Corp</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ marginTop: '12px', padding: '10px', background: '#f3f4f6', borderRadius: '6px', fontSize: '13px' }}>
                                    <strong>Email Template Example:</strong><br/>
                                    <code style={{ display: 'block', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                                        Dear {'{name}'},\n\nYour aging report for {'{company_name}'} (Code: {'{ledger_code}'}) is attached.\n\nBest regards
                                    </code>
                                </div>
                            </div>

                            <div className="instruction-section warning">
                                <h4>⚠️ Important Notes</h4>
                                <ul>
                                    <li>Column names must match exactly (case-sensitive)</li>
                                    <li>ledger_code, name, and email are required fields</li>
                                    <li>File must be in Excel format (.xlsx or .xls)</li>
                                    <li>Maximum file size: 10MB</li>
                                    <li><code>mailto:</code> links, tabs and duplicate addresses are cleaned automatically</li>
                                </ul>
                            </div>
                        </div>
                        )}

                        <div className="modal-actions">
                            {!uploadResult && (
                                <button
                                    type="button"
                                    onClick={downloadSampleTemplate}
                                    className="btn-secondary"
                                >
                                    📥 Download Sample Template
                                </button>
                            )}
                            <label
                                className="btn-primary"
                                style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
                            >
                                {uploading
                                    ? '⏳ Uploading...'
                                    : uploadResult ? '📤 Upload Another File' : '📤 Choose File & Upload'}
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleBulkUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                />
                            </label>
                            <button
                                type="button"
                                onClick={closeBulkUploadModal}
                                className="btn-secondary"
                            >
                                {uploadResult ? 'Close' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomersTab;
